/**
 * Selected work.
 *
 * `kind` is the label above each title. It describes who the work was for - a
 * sector, a client type, or "Own product" - so changing it changes what the page
 * claims about the engagement. Do not relabel an entry without checking that the
 * new label is true.
 *
 * Rules for anything added here:
 *   • No invented metrics. A number appears only if it has been measured.
 *   • No client name without written permission.
 *   • No testimonial that was not actually given.
 *   • A privacy or security claim must match what the software actually does.
 *     "Your photo never leaves the browser" is a promise, not a flourish.
 */

export interface WorkItem {
  readonly id: string;
  readonly title: string;
  /** The problem, in the client's language rather than ours. */
  readonly problem: string;
  /** What we built. */
  readonly response: string;
  /** Technologies, for readers who care. */
  readonly stack: readonly string[];
  /** Short label describing the engagement type. */
  readonly kind: string;
  /** Optional public link. Omit for private work. */
  readonly href?: string;
}

export const work: readonly WorkItem[] = [
  {
    id: 'family-paw-life',
    title: 'Family Paw Life',
    kind: 'NPO product',
    problem:
      'Shelters have cats and dogs waiting for homes, and there are people who would happily give them one. The hard part is the two ever finding each other.',
    response:
      'A place where shelters can list the animals in their care and prospective owners can find them, so a rehoming starts with the right match rather than luck.',
    // Left empty deliberately: the stack has not been confirmed, and an entry here
    // renders as a public claim about how the product is built. Fill it in or leave it.
    stack: [],
  },
  {
    id: 'sequence',
    title: 'Sequence',
    kind: 'Logistics company',
    problem:
      'South African logistics operators plan multi-vehicle delivery routes by hand, in spreadsheets, against constraints - vehicle capacity, delivery windows, driver shifts - that a person cannot hold in their head at scale.',
    response:
      'A fleet route optimisation system: a constraint model of the real operating rules, a solver that produces schedules against them, and an operator-facing interface for reviewing and adjusting the result before it goes out.',
    stack: ['Go', 'Python', 'React', 'PostgreSQL', 'Google Cloud'],
  },
  {
    id: 'pic-by-brick',
    title: 'Pic by Brick',
    kind: 'Own product',
    problem:
      'Turning a photograph into something you can actually build means matching every pixel to a colour that exists as a real brick, the way an eye judges colour rather than the way a computer subtracts numbers.',
    response:
      'Pic by Brick turns any photo into a buildable brick mosaic, matched to 73 real colours the way your eye sees them, with the exact parts to build it for real. It all runs in your browser, so your photo never leaves it.',
    stack: ['TypeScript', 'React', 'Firebase', 'Google Cloud'],
  },
];
